<template>
  <div v-if="show" class="christmas-overlay">
    <!-- Canvas pour les confettis -->
    <canvas ref="confettiCanvas" class="confetti-canvas"></canvas>

    <!-- Message et cœur -->
    <div class="christmas-message">
      <div class="beating-heart">❤️</div>
      <h1 class="christmas-text">Joyeux Noël doudoue !</h1>

      <!-- Bouton pour lancer le son si bloqué -->
      <div v-if="audioBlocked" class="audio-blocked-message">
        <q-btn
            color="red"
            icon="play_arrow"
            label="Clique pour activer le son 🎵 (gros bouton rouge)"
            @click="playCurrentAudio"
            size="lg"
            class="pulse-button"
        />
      </div>

      <div class="snowflakes">
        <div class="snowflake">❅</div>
        <div class="snowflake">❆</div>
        <div class="snowflake">❅</div>
        <div class="snowflake">❆</div>
        <div class="snowflake">❅</div>
      </div>
    </div>

    <!-- Photo dans un cœur en bas à droite -->
    <div class="heart-photo-container">
      <div class="heart-shape">
        <img :src="photoPath" alt="Photo" class="heart-image" />
      </div>
      <div class="heart-glow"></div>
    </div>

    <!-- Contrôles audio avec info playlist -->
    <div class="audio-controls">
      <div class="audio-info" v-if="currentTrack >= 0">
        <div class="track-info">
          🎵 {{ currentTrack + 1 }}/{{ playlist.length }}
        </div>
      </div>
      <q-btn
          round
          :color="isMuted ? 'grey' : 'red'"
          :icon="isMuted ? 'volume_off' : 'volume_up'"
          @click="toggleMute"
          size="md"
          class="mute-btn"
      >
        <q-tooltip>{{ isMuted ? 'Activer le son' : 'Couper le son' }}</q-tooltip>
      </q-btn>
      <q-btn
          round
          color="primary"
          icon="skip_next"
          @click="playNextTrack"
          size="sm"
          class="next-btn"
          v-if="playlist.length > 1"
      >
        <q-tooltip>Chanson suivante</q-tooltip>
      </q-btn>
    </div>

    <!-- Bouton pour fermer -->
    <q-btn
        class="close-btn"
        round
        color="white"
        text-color="red"
        icon="close"
        @click="close"
        size="md"
    >
      <q-tooltip>Fermer</q-tooltip>
    </q-btn>
  </div>
</template>

<script>
import AppNotificationService from "../services/utils/appNotificationService";

export default {
  name: 'christmas',
  data() {
    return {
      show: false,
      confetti: [],
      animationId: null,
      isMuted: false,
      audioBlocked: false,
      audio: null,
      currentTrack: -1,
      // Configuration de la playlist - Ajoutez vos fichiers ici
      playlist: [
        'christmas/coin.mp3',
        'christmas/coin.mp3',
        'christmas/maria.mp3'
      ],
      // Chemin vers votre photo (à placer dans /public/)
      photoPath: 'christmas/photo.jpg'
    }
  },
  methods: {
    trigger() {
      this.show = true
      this.$nextTick(() => {
        this.initConfetti()
        this.animateConfetti()
        this.initAudio()
      })

      // Auto-fermeture après 60 secondes (augmenté pour la playlist)
      setTimeout(() => {
        this.close()
      }, 1200000)
    },

    initAudio() {
      // Créer l'élément audio si pas encore créé
      if (!this.audio) {
        this.audio = new Audio()
        this.audio.volume = 0.5

        // Événement quand une chanson se termine
        this.audio.addEventListener('ended', () => {
          console.log('🎵 Chanson terminée, passage à la suivante...')
          this.playNextTrack()
        })

        // Logs pour debug
        this.audio.addEventListener('canplay', () => {
          console.log('✅ Audio prêt à jouer')
        })

        this.audio.addEventListener('error', (e) => {
          console.error('❌ Erreur de chargement audio:', e)
          AppNotificationService.pushError(`Erreur: Impossible de charger ${this.playlist[this.currentTrack]}`)
          // Essayer la chanson suivante en cas d'erreur
          this.playNextTrack()
        })

        this.audio.addEventListener('playing', () => {
          console.log('🎵 Audio en cours de lecture')
          this.audioBlocked = false
        })
      }

      // Démarrer la première chanson
      this.currentTrack = 0
      this.loadTrack(this.currentTrack)
    },

    loadTrack(trackIndex) {
      if (trackIndex >= 0 && trackIndex < this.playlist.length) {
        this.currentTrack = trackIndex
        this.audio.src = this.playlist[trackIndex]
        console.log(`📀 Chargement de la piste ${trackIndex + 1}/${this.playlist.length}: ${this.playlist[trackIndex]}`)
        this.playCurrentAudio()
      }
    },

    playCurrentAudio() {
      if (!this.audio) {
        this.initAudio()
        return
      }

      console.log('Tentative de lecture audio...')

      this.audio.play()
          .then(() => {
            console.log('✅ Audio démarré avec succès')
            this.isMuted = false
            this.audioBlocked = false
            //AppNotificationService.pushInfo(`🎵 Piste ${this.currentTrack + 1}/${this.playlist.length}`)
          })
          .catch(error => {
            console.warn('⚠️ Autoplay bloqué:', error)
            this.audioBlocked = true
            this.isMuted = true
            AppNotificationService.pushWarning('Clique sur le bouton pour activer le son !')
          })
    },

    playNextTrack() {
      if (this.currentTrack < this.playlist.length - 1) {
        // Passer à la chanson suivante
        this.loadTrack(this.currentTrack + 1)
      } else {
        // Recommencer depuis le début (loop de la playlist)
        console.log('🔄 Fin de la playlist, retour au début')
        this.loadTrack(0)
      }
    },

    toggleMute() {
      if (!this.audio) return

      this.isMuted = !this.isMuted

      if (this.isMuted) {
        this.audio.pause()
        console.log('🔇 Audio en pause')
      } else {
        this.playCurrentAudio()
      }
    },

    close() {
      this.show = false

      if (this.audio) {
        this.audio.pause()
        this.audio.currentTime = 0
      }

      if (this.animationId) {
        cancelAnimationFrame(this.animationId)
      }

      this.confetti = []
      this.audioBlocked = false
      this.currentTrack = -1
    },

    initConfetti() {
      const canvas = this.$refs.confettiCanvas
      if (!canvas) return

      canvas.width = window.innerWidth
      canvas.height = window.innerHeight

      // Créer les confettis
      const colors = ['#ff0000', '#00ff00', '#0000ff', '#ffff00', '#ff00ff', '#00ffff', '#ffd700', '#ff69b4']

      for (let i = 0; i < 150; i++) {
        this.confetti.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height - canvas.height,
          r: Math.random() * 6 + 4,
          d: Math.random() * 150,
          color: colors[Math.floor(Math.random() * colors.length)],
          tilt: Math.random() * 10 - 10,
          tiltAngleIncremental: Math.random() * 0.07 + 0.05,
          tiltAngle: 0
        })
      }
    },

    animateConfetti() {
      const canvas = this.$refs.confettiCanvas
      if (!canvas) return

      const ctx = canvas.getContext('2d')

      const draw = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height)

        this.confetti.forEach((c, i) => {
          ctx.beginPath()
          ctx.lineWidth = c.r / 2
          ctx.strokeStyle = c.color
          ctx.moveTo(c.x + c.tilt + c.r, c.y)
          ctx.lineTo(c.x + c.tilt, c.y + c.tilt + c.r)
          ctx.stroke()

          // Mettre à jour la position
          c.tiltAngle += c.tiltAngleIncremental
          c.y += (Math.cos(c.d) + 3 + c.r / 2) / 2
          c.tilt = Math.sin(c.tiltAngle - i / 3) * 15

          // Réinitialiser si en bas de l'écran
          if (c.y > canvas.height) {
            this.confetti[i] = {
              ...c,
              x: Math.random() * canvas.width,
              y: -10,
              tilt: Math.random() * 10 - 10
            }
          }
        })

        this.animationId = requestAnimationFrame(draw)
      }

      draw()
    }
  },

  beforeUnmount() {
    if (this.audio) {
      this.audio.pause()
      this.audio.currentTime = 0
      this.audio.removeEventListener('ended', this.playNextTrack)
      this.audio = null
    }

    if (this.animationId) {
      cancelAnimationFrame(this.animationId)
    }
  }
}
</script>

<style scoped lang="scss">
.christmas-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  pointer-events: none;
}

.confetti-canvas {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.christmas-message {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  z-index: 10000;
}

.beating-heart {
  font-size: 120px;
  animation: heartbeat 1.5s ease-in-out infinite;
  filter: drop-shadow(0 0 20px rgba(255, 0, 0, 0.8));
}

@keyframes heartbeat {
  0%, 100% {
    transform: scale(1);
  }
  10%, 30% {
    transform: scale(1.2);
  }
  20%, 40% {
    transform: scale(1.1);
  }
}

.christmas-text {
  font-size: 72px;
  font-weight: bold;
  background: linear-gradient(45deg, #ff0000, #00ff00, #0000ff, #ffff00);
  background-size: 300% 300%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: gradient 3s ease infinite, glow 2s ease-in-out infinite;
  text-shadow: 0 0 30px rgba(255, 255, 255, 0.8);
  margin: 20px 0;
}

@keyframes gradient {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

@keyframes glow {
  0%, 100% {
    filter: drop-shadow(0 0 10px rgba(255, 215, 0, 0.8));
  }
  50% {
    filter: drop-shadow(0 0 30px rgba(255, 215, 0, 1));
  }
}

.audio-blocked-message {
  margin: 30px 0;
  pointer-events: all;
}

.pulse-button {
  animation: pulse-big 1.5s ease-in-out infinite;
  font-weight: bold;
}

@keyframes pulse-big {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}

.snowflakes {
  position: absolute;
  top: -50px;
  left: 0;
  right: 0;
  height: 0;
}

.snowflake {
  position: absolute;
  top: -10px;
  color: white;
  font-size: 30px;
  animation: fall linear infinite;
  text-shadow: 0 0 10px rgba(255, 255, 255, 0.8);
}

.snowflake:nth-child(1) {
  left: 10%;
  animation-duration: 8s;
  animation-delay: 0s;
}

.snowflake:nth-child(2) {
  left: 30%;
  animation-duration: 10s;
  animation-delay: 2s;
}

.snowflake:nth-child(3) {
  left: 50%;
  animation-duration: 12s;
  animation-delay: 4s;
}

.snowflake:nth-child(4) {
  left: 70%;
  animation-duration: 9s;
  animation-delay: 1s;
}

.snowflake:nth-child(5) {
  left: 90%;
  animation-duration: 11s;
  animation-delay: 3s;
}

@keyframes fall {
  from {
    top: -50px;
    opacity: 1;
  }
  to {
    top: 100vh;
    opacity: 0.3;
  }
}

/* Photo dans un cœur en bas à droite */
.heart-photo-container {
  position: fixed;
  bottom: 30px;
  right: 30px;
  z-index: 10000;
  pointer-events: all;
}

.heart-shape {
  position: relative;
  width: 150px;
  height: 150px;
  animation: heartbeat-photo 2s ease-in-out infinite;
  filter: drop-shadow(0 0 30px rgba(255, 0, 100, 0.8));
}

.heart-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  /* Créer la forme de cœur avec clip-path */
  clip-path: path('M12,21.35L10.55,20.03C5.4,15.36 2,12.27 2,8.5C2,5.41 4.42,3 7.5,3C9.24,3 10.91,3.81 12,5.08C13.09,3.81 14.76,3 16.5,3C19.58,3 22,5.41 22,8.5C22,12.27 18.6,15.36 13.45,20.03L12,21.35Z');
  /* Alternative avec une forme simplifiée */
  clip-path: polygon(
          50% 15%,
          25% 0%,
          0% 25%,
          0% 50%,
          50% 100%,
          100% 50%,
          100% 25%,
          75% 0%
  );
  /* Bordure du cœur */
  border: 3px solid rgba(255, 255, 255, 0.8);
  border-radius: 0;
}

.heart-glow {
  position: absolute;
  top: -10px;
  left: -10px;
  right: -10px;
  bottom: -10px;
  background: radial-gradient(circle, rgba(255, 0, 100, 0.3), transparent 70%);
  animation: glow-pulse 2s ease-in-out infinite;
  pointer-events: none;
  z-index: -1;
}

@keyframes heartbeat-photo {
  0%, 100% {
    transform: scale(1);
  }
  25% {
    transform: scale(1.05);
  }
  50% {
    transform: scale(1);
  }
  75% {
    transform: scale(1.05);
  }
}

@keyframes glow-pulse {
  0%, 100% {
    opacity: 0.5;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.1);
  }
}

.audio-controls {
  position: absolute;
  top: 20px;
  left: 20px;
  z-index: 10001;
  pointer-events: all;
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: flex-start;
}

.audio-info {
  background: rgba(0, 0, 0, 0.7);
  padding: 8px 15px;
  border-radius: 20px;
  backdrop-filter: blur(10px);
}

.track-info {
  color: white;
  font-size: 14px;
  font-weight: bold;
  text-shadow: 0 0 10px rgba(255, 255, 255, 0.5);
}

.mute-btn {
  animation: pulse-audio 2s ease-in-out infinite;
}

.next-btn {
  margin-top: 5px;
}

@keyframes pulse-audio {
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 0 10px rgba(255, 0, 0, 0.3);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 0 20px rgba(255, 0, 0, 0.6);
  }
}

.close-btn {
  position: absolute;
  top: 20px;
  right: 20px;
  z-index: 10001;
  pointer-events: all;
  box-shadow: 0 0 20px rgba(255, 0, 0, 0.5);
}

/* Responsive */
@media (max-width: 768px) {
  .heart-photo-container {
    bottom: 20px;
    right: 20px;
  }

  .heart-shape {
    width: 100px;
    height: 100px;
  }

  .christmas-text {
    font-size: 48px;
  }

  .beating-heart {
    font-size: 80px;
  }
}
</style>